import { Group, Text } from '@mantine/core';
import { colors } from '../../theme/tokens';

export type StatusDotState = 'running' | 'idle' | 'error' | 'warning';
export type StatusDotSize = 'xs' | 'sm' | 'md';

export interface StatusDotProps {
  state: StatusDotState;
  size?: StatusDotSize;
  /** Optional label rendered after the dot. */
  label?: string;
  /** Pulse animation on/off. Defaults to true for `running` and `warning`. */
  pulse?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const STATE_TO_COLOR: Record<StatusDotState, string> = {
  running: colors.neon,
  idle: 'rgb(var(--c-txt-muted))',
  error: colors.hudRed,
  warning: colors.hudAmber,
};

/**
 * Non-color cue (a11y): each state also has a distinct SHAPE so the state is
 * never conveyed by hue alone — critical for colorblind users. Shapes are drawn
 * on the inner mark so the pulse halo (box-shadow on the outer wrapper) is
 * unaffected.
 *   running → filled circle · idle → hollow circle
 *   warning → triangle       · error → diamond
 */
type MarkShape = 'filled' | 'hollow' | 'triangle' | 'diamond';
const STATE_TO_SHAPE: Record<StatusDotState, MarkShape> = {
  running: 'filled',
  idle: 'hollow',
  warning: 'triangle',
  error: 'diamond',
};

/** Human-readable state name — used as the accessible name when no `label` is given. */
const STATE_TO_A11Y_LABEL: Record<StatusDotState, string> = {
  running: 'Running',
  idle: 'Idle',
  error: 'Error',
  warning: 'Warning',
};

function markStyle(shape: MarkShape, color: string, px: number): React.CSSProperties {
  const base: React.CSSProperties = { display: 'block', width: px, height: px };
  switch (shape) {
    case 'filled':
      return { ...base, backgroundColor: color, borderRadius: '50%' };
    case 'hollow':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: `1.5px solid ${color}`,
        borderRadius: '50%',
      };
    case 'triangle':
      return { ...base, backgroundColor: color, clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' };
    case 'diamond':
      return {
        ...base,
        backgroundColor: color,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
      };
  }
}

const SIZE_TO_PX: Record<StatusDotSize, number> = { xs: 6, sm: 8, md: 10 };

/**
 * StatusDot — small colored circle used to indicate range state, agent
 * connectivity, or attack phase status. When `pulse` is true the dot emits
 * a softly animated halo via the `forge-status-pulse` `box-shadow` keyframes
 * defined in styles/global.css.
 */
export function StatusDot({
  state,
  size = 'sm',
  label,
  pulse,
  className,
  style,
}: StatusDotProps) {
  const color = STATE_TO_COLOR[state];
  const px = SIZE_TO_PX[size];
  const shape = STATE_TO_SHAPE[state];
  const shouldPulse = pulse ?? (state === 'running' || state === 'warning');

  // When a visible `label` is present it carries the meaning, so the mark stays
  // decorative. Otherwise the mark itself exposes the state to assistive tech.
  const a11y: React.HTMLAttributes<HTMLSpanElement> = label
    ? { 'aria-hidden': true }
    : { role: 'img', 'aria-label': STATE_TO_A11Y_LABEL[state] };

  return (
    <Group
      gap={6}
      align="center"
      wrap="nowrap"
      {...(className !== undefined ? { className } : {})}
      {...(style !== undefined ? { style } : {})}
    >
      <span
        data-state={state}
        data-shape={shape}
        {...a11y}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: px,
          height: px,
          borderRadius: '50%',
          // `color` drives `currentColor` in the pulse keyframes below.
          color,
          boxShadow: shouldPulse ? '0 0 0 0 currentColor' : 'none',
          animation: shouldPulse ? 'forge-status-pulse 1.6s ease-out infinite' : 'none',
        }}
      >
        <span aria-hidden style={markStyle(shape, color, px)} />
      </span>
      {label ? (
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      ) : null}
    </Group>
  );
}
